import "react-app-polyfill/ie9";
import "react-app-polyfill/ie11";
import "react-app-polyfill/stable";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { createBrowserRouter } from "react-router";
import { RouterProvider } from "react-router/dom";

import * as Sentry from "@sentry/react";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  RadialLinearScale,
  TimeScale,
  TimeSeriesScale,
  Title,
  Tooltip,
} from "chart.js";
import "chartjs-adapter-date-fns";
// Fix leaflet icon issue https://github.com/PaulLeCam/react-leaflet/issues/808
// @ts-ignore
import L from "leaflet";
import markerIcon2 from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import "leaflet/dist/leaflet.css";
import "unorm";
import "url-search-params-polyfill";

import App from "./components/App";
import "./serviceWorkerBridge";
import { store } from "./store";
import { installTranslateGuard } from "./utils/translateGuard";

installTranslateGuard();

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Filler,
  RadialLinearScale,
  TimeScale,
  TimeSeriesScale,
  Title,
  Tooltip,
);

delete (L.Icon.Default.prototype as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2,
  shadowUrl: markerShadow,
});

// Source: https://github.com/jserz/js_piece/blob/master/DOM/ParentNode/append()/append().md
((arr) => {
  arr.forEach((item) => {
    if (Object.hasOwn(item, "append")) {
      return;
    }
    Object.defineProperty(item, "append", {
      configurable: true,
      enumerable: true,
      writable: true,
      value: function append({ ...args }) {
        const argArr = Array.prototype.slice.call(args);
        const docFrag = document.createDocumentFragment();

        argArr.forEach((argItem) => {
          const isNode = argItem instanceof Node;
          docFrag.appendChild(
            isNode ? argItem : document.createTextNode(String(argItem)),
          );
        });

        this.appendChild(docFrag);
      },
    });
  });
})([Element.prototype, Document.prototype, DocumentFragment.prototype]);

// Set up umami tracking in production
const umamiScript = import.meta.env.VITE_UMAMI_SCRIPT;
const umamiWebsiteId = import.meta.env.VITE_UMAMI_WEBSITE_ID;

if (umamiScript && umamiWebsiteId) {
  const script = document.createElement("script");
  script.src = umamiScript;
  script.defer = true;
  script.dataset.websiteId = umamiWebsiteId;
  script.id = "umami-script";
  document.head.appendChild(script);
}

if (import.meta.env.VITE_IS_PRODUCTION === "true") {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      // @ts-ignore
      Sentry.browserTracingIntegration(),
      // @ts-ignore
      Sentry.replayIntegration(),
    ],
    environment: import.meta.env.MODE,
    normalizeDepth: 10,
    release: import.meta.env.VITE_GIT_SHA,
    maxBreadcrumbs: 50,
    denyUrls: [
      /support\.simplydo\.co\.uk/i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^webkit-masked-url:\//i,
    ],
    replaysOnErrorSampleRate: 1.0,
  });
}

const router = createBrowserRouter([{ path: "*", element: <App /> }]);
const container = document.getElementById("root")!;
const root = createRoot(container);
root.render(
  <Provider store={store}>
    {/* Without this, navigations run in a transition and lazy routes keep the
        previous page on screen instead of showing their Suspense fallback */}
    <RouterProvider router={router} useTransitions={false} />
  </Provider>,
);
